import type { Partner } from "@expert/shared-types";

import { getSharedApiClient } from "@/shared-utils";

import type { DataKeys } from "../types";

const getDataKeysApi = () => {
    return getSharedApiClient().extend(({ prefix }) => ({
        prefix: `${prefix}/api/v1/encryption/data-key`,
    }));
};

/**
 * Generates a fresh KMS data key pair for the given encryption context (`GET .../generate/...`).
 *
 * Prefer `encryptWithCache` from `../dataKeyService` in all chat code — it dedupes requests and
 * caches `DataKeys` in React Query. Call this low-level function only from `dataKeyService` or tests.
 *
 * @internal
 */
export async function generateDataKey(partner: Partner, context: string): Promise<DataKeys> {
    const path = `generate/${encodeURIComponent(partner)}/${encodeURIComponent(context)}`;
    return await getDataKeysApi().get(path).json<DataKeys>();
}

/**
 * Unwraps a KMS-wrapped data key for the given encryption context (`GET .../decrypt/...`).
 *
 * Prefer `decryptWithCache` from `../dataKeyService` in all chat code — it dedupes requests and
 * caches `DataKeys` in React Query. Call this low-level function only from `dataKeyService` or tests.
 *
 * @internal
 */
export async function decryptDataKey(partner: Partner, context: string, encryptedDataKey: string): Promise<DataKeys> {
    const queryParams = new URLSearchParams({ encryptedDataKey });
    const path = `decrypt/${encodeURIComponent(partner)}/${encodeURIComponent(context)}?${queryParams.toString()}`;
    return await getDataKeysApi().get(path).json<DataKeys>();
}
